import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import Pagination from "../ui/pagination";
import {
	Loader2, RefreshCw, Search, Check, X, SkipForward,
	ChevronDown, ChevronRight, Keyboard, AlertTriangle,
	CheckCircle2, Package, Link2, Zap,
} from "lucide-react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { STORAGE_KEY } from "../../lib/constants";

type QueueType = "mrp" | "packing" | "mapping";

interface QueueStats {
	queue_type: string;
	pending: number;
	accepted: number;
	rejected: number;
	skipped: number;
	high_priority: number;
	quick_wins: number;
	reviewed_today: number;
}

export default function DataReviewTab() {
	const [loading, setLoading] = useState(false);
	const [populating, setPopulating] = useState(false);
	const [items, setItems] = useState<any[]>([]);
	const [stats, setStats] = useState<QueueStats[]>([]);
	const [branches, setBranches] = useState<any[]>([]);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [total, setTotal] = useState(0);

	const [queueType, setQueueType] = useState<QueueType>("mrp");
	const [priority, setPriority] = useState("all");
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [quickWins, setQuickWins] = useState(false);
	const [branchFilter, setBranchFilter] = useState("all");

	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [focusedIndex, setFocusedIndex] = useState(0);
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [customMrpId, setCustomMrpId] = useState<string | null>(null);
	const [customValue, setCustomValue] = useState("");
	const [showShortcuts, setShowShortcuts] = useState(false);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const [editingPackingId, setEditingPackingId] = useState<string | null>(null);
	const [editingPackingValue, setEditingPackingValue] = useState("");

	const listRef = useRef<HTMLDivElement>(null);
	const customInputRef = useRef<HTMLInputElement>(null);
	const editingPackingRef = useRef<HTMLInputElement>(null);
	const { toast } = useToast();

	const getUserInfo = () => {
		const user = localStorage.getItem(STORAGE_KEY);
		if (!user) return { email: "unknown@pillplus.com" };
		const parsed = JSON.parse(user);
		return { email: parsed.email || "unknown@pillplus.com", name: parsed.displayName || parsed.name };
	};

	// ─── Data Fetching ────────────────────────────────────────────────────

	const fetchStats = async () => {
		try {
			const res = await api.b2cMgmtGetReviewStats();
			if (res?.success) {
				setStats(res.data.queues || []);
				setBranches(res.data.branches || []);
			}
		} catch {}
	};

	const fetchQueue = useCallback(async () => {
		setLoading(true);
		try {
			const params: Record<string, any> = { type: queueType, page, limit: 25 };
			if (priority !== "all") params.priority = priority;
			if (search) params.search = search;
			if (quickWins) params.quick_wins = "true";
			if (branchFilter !== "all") params.branch_id = branchFilter;

			const res = await api.b2cMgmtGetReviewQueue(params);
			if (res?.success) {
				setItems(res.data || []);
				setTotal(res.pagination?.total || 0);
				setTotalPages(res.pagination?.totalPages || 0);
				setFocusedIndex(0);
				setSelectedIds(new Set());
				setExpandedId(null);
				setEditingPackingId(null);
				setEditingPackingValue("");
			}
		} catch {
			toast({ title: "Error", description: "Failed to load review queue", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	}, [queueType, page, priority, search, quickWins, branchFilter]);

	useEffect(() => { fetchStats(); }, []);
	useEffect(() => { fetchQueue(); }, [fetchQueue]);

	// ─── Actions ──────────────────────────────────────────────────────────

	const handleAccept = async (ids: string[]) => {
		if (ids.length === 0) return;
		setActionLoading("accept");
		try {
			const res = await api.b2cMgmtAcceptReview({ ids, user: getUserInfo() });
			if (res?.success) {
				toast({ title: "Applied", description: `${res.data.accepted} item(s) updated` });
				setSelectedIds(new Set());
				fetchQueue();
				fetchStats();
			} else {
				toast({ title: "Error", description: res?.error, variant: "destructive" });
			}
		} catch {
			toast({ title: "Error", description: "Failed to apply", variant: "destructive" });
		} finally {
			setActionLoading(null);
		}
	};

	const handleReject = async (ids: string[]) => {
		if (ids.length === 0) return;
		setActionLoading("reject");
		try {
			const res = await api.b2cMgmtRejectReview({ ids, user: getUserInfo() });
			if (res?.success) {
				toast({ title: "Kept current", description: `${ids.length} item(s) marked as correct` });
				setSelectedIds(new Set());
				fetchQueue();
				fetchStats();
			}
		} catch {
			toast({ title: "Error", description: "Failed to reject", variant: "destructive" });
		} finally {
			setActionLoading(null);
		}
	};

	const handleConfirmMapping = async (ids: string[]) => {
		if (ids.length === 0) return;
		setActionLoading("confirm");
		try {
			const res = await api.b2cMgmtConfirmMapping({ ids, user: getUserInfo() });
			if (res?.success) {
				toast({ title: "Confirmed", description: `${ids.length} mapping(s) confirmed as correct` });
				setSelectedIds(new Set());
				fetchQueue();
				fetchStats();
			}
		} catch {
			toast({ title: "Error", description: "Failed to confirm", variant: "destructive" });
		} finally {
			setActionLoading(null);
		}
	};

	const handleSkip = async (ids: string[]) => {
		if (ids.length === 0) return;
		try {
			await api.b2cMgmtSkipReview({ ids });
			fetchQueue();
			fetchStats();
		} catch {}
	};

	const handleCustomApply = async () => {
		if (!customMrpId || !customValue) return;
		setActionLoading("custom");
		try {
			const res = await api.b2cMgmtApplyCustomValue({
				id: customMrpId,
				custom_value: customValue,
				user: getUserInfo(),
			});
			if (res?.success) {
				toast({ title: "Applied", description: "Custom value set" });
				setCustomMrpId(null);
				setCustomValue("");
				fetchQueue();
				fetchStats();
			}
		} catch {
			toast({ title: "Error", description: "Failed to apply custom value", variant: "destructive" });
		} finally {
			setActionLoading(null);
		}
	};

	const handleSavePacking = async (id: string, suggestedValue: string) => {
		const trimmed = editingPackingValue.trim();
		if (!trimmed) return;
		if (trimmed.toLowerCase() === (suggestedValue || "").trim().toLowerCase()) {
			// Accept the suggestion as-is
			await handleAccept([id]);
		} else {
			// Apply custom value
			setActionLoading("custom");
			try {
				const res = await api.b2cMgmtApplyCustomValue({
					id,
					custom_value: trimmed,
					user: getUserInfo(),
				});
				if (res?.success) {
					toast({ title: "Applied", description: "Packing updated" });
					fetchQueue();
					fetchStats();
				} else {
					toast({ title: "Error", description: res?.error, variant: "destructive" });
				}
			} catch {
				toast({ title: "Error", description: "Failed to update packing", variant: "destructive" });
			} finally {
				setActionLoading(null);
			}
		}
		setEditingPackingId(null);
		setEditingPackingValue("");
	};

	const handlePopulate = async () => {
		setPopulating(true);
		try {
			const res = await api.b2cMgmtPopulateReviewQueue("all");
			if (res?.success) {
				toast({ title: "Queue refreshed", description: `MRP: ${res.data.mrp_items || 0}, Packing: ${res.data.packing_items || 0}, Mapping: ${res.data.mapping_items || 0}` });
				fetchQueue();
				fetchStats();
			} else {
				toast({ title: "Error", description: res?.error, variant: "destructive" });
			}
		} catch {
			toast({ title: "Error", description: "Failed to populate queue", variant: "destructive" });
		} finally {
			setPopulating(false);
		}
	};

	// ─── Selection ────────────────────────────────────────────────────────

	const toggleSelect = (id: string) => {
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const selectAll = () => {
		if (selectedIds.size === items.length) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(items.map(i => i.id)));
		}
	};

	// ─── Keyboard Navigation ──────────────────────────────────────────────

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Don't capture when typing in input
			if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
			if (customMrpId) return; // Custom input mode

			switch (e.key) {
				case "ArrowDown":
				case "j":
					e.preventDefault();
					setFocusedIndex(prev => Math.min(prev + 1, items.length - 1));
					break;
				case "ArrowUp":
				case "k":
					e.preventDefault();
					setFocusedIndex(prev => Math.max(prev - 1, 0));
					break;
				case "a":
					if (items[focusedIndex]) {
						e.preventDefault();
						if (selectedIds.size > 0) {
							handleAccept(Array.from(selectedIds));
						} else {
							handleAccept([items[focusedIndex].id]);
						}
					}
					break;
				case "r":
					if (items[focusedIndex]) {
						e.preventDefault();
						if (queueType === "mapping") {
							if (selectedIds.size > 0) {
								handleConfirmMapping(Array.from(selectedIds));
							} else {
								handleConfirmMapping([items[focusedIndex].id]);
							}
						} else {
							if (selectedIds.size > 0) {
								handleReject(Array.from(selectedIds));
							} else {
								handleReject([items[focusedIndex].id]);
							}
						}
					}
					break;
				case "s":
					if (items[focusedIndex]) {
						e.preventDefault();
						handleSkip([items[focusedIndex].id]);
					}
					break;
				case " ":
					if (items[focusedIndex]) {
						e.preventDefault();
						toggleSelect(items[focusedIndex].id);
					}
					break;
				case "Enter":
					e.preventDefault();
					if (items[focusedIndex]) {
						setExpandedId(expandedId === items[focusedIndex].id ? null : items[focusedIndex].id);
					}
					break;
				case "?":
					e.preventDefault();
					setShowShortcuts(!showShortcuts);
					break;
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [items, focusedIndex, selectedIds, expandedId, customMrpId, queueType]);

	// Auto-scroll focused item into view
	useEffect(() => {
		const el = listRef.current?.querySelector(`[data-index="${focusedIndex}"]`);
		if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
	}, [focusedIndex]);

	// ─── Stats Helpers ────────────────────────────────────────────────────

	const getQueueStat = (type: string): QueueStats | undefined => stats.find(s => s.queue_type === type);
	const currentStats = getQueueStat(queueType);
	const totalPending = stats.reduce((sum, s) => sum + Number(s.pending || 0), 0);
	const totalReviewedToday = stats.reduce((sum, s) => sum + Number(s.reviewed_today || 0), 0);

	const getPriorityBadge = (p: string) => {
		if (p === "high") return <Badge variant="destructive" className="text-xs">HIGH</Badge>;
		if (p === "medium") return <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">MED</Badge>;
		return <Badge variant="outline" className="text-xs">LOW</Badge>;
	};

	// ─── Render ───────────────────────────────────────────────────────────

	return (
		<div className="space-y-4">
			{/* Keyboard shortcuts help */}
			{showShortcuts && (
				<Card className="dark:bg-gray-800 dark:border-gray-700 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20">
					<CardContent className="p-4">
						<div className="flex items-center justify-between mb-2">
							<span className="font-medium dark:text-gray-200">Keyboard Shortcuts</span>
							<Button variant="ghost" size="sm" onClick={() => setShowShortcuts(false)}><X className="h-4 w-4" /></Button>
						</div>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
							<div><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">↑↓</kbd> or <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">j/k</kbd> Navigate</div>
							<div><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">a</kbd> Accept suggestion</div>
							<div><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">r</kbd> {queueType === "mapping" ? "Confirm link" : "Keep current"}</div>
							<div><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">s</kbd> Skip</div>
							<div><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Space</kbd> Toggle select</div>
							<div><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Enter</kbd> Expand/collapse</div>
							<div><kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">?</kbd> Toggle shortcuts</div>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Progress bar + Stats */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-3">
				<Card className="md:col-span-2 dark:bg-gray-800 dark:border-gray-700">
					<CardContent className="p-4">
						<div className="flex items-center justify-between mb-2">
							<span className="text-sm font-medium dark:text-gray-200">Today's Progress</span>
							<span className="text-sm text-gray-500 dark:text-gray-400">{totalReviewedToday} reviewed today</span>
						</div>
						<div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
							<div
								className="h-full bg-green-500 rounded-full transition-all"
								style={{ width: `${totalPending > 0 ? Math.min((totalReviewedToday / (totalPending + totalReviewedToday)) * 100, 100) : 0}%` }}
							/>
						</div>
						<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
							{totalPending} pending across all queues
						</div>
					</CardContent>
				</Card>
				<Card className="dark:bg-gray-800 dark:border-gray-700">
					<CardContent className="p-4 text-center">
						<div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{currentStats ? Number(currentStats.pending) : 0}</div>
						<div className="text-xs text-gray-500 dark:text-gray-400">Pending in queue</div>
					</CardContent>
				</Card>
				<Card className="dark:bg-gray-800 dark:border-gray-700">
					<CardContent className="p-4 text-center">
						<div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{currentStats ? Number(currentStats.quick_wins) : 0}</div>
						<div className="text-xs text-gray-500 dark:text-gray-400">Quick wins (100%)</div>
					</CardContent>
				</Card>
			</div>

			{/* Queue type tabs + Filters */}
			<Card className="dark:bg-gray-800 dark:border-gray-700">
				<CardContent className="p-4 space-y-3">
					<div className="flex items-center gap-2">
						{(["mrp", "packing", "mapping"] as QueueType[]).map(type => {
							const stat = getQueueStat(type);
							const pending = stat ? Number(stat.pending) : 0;
							const icon = type === "mrp" ? <AlertTriangle className="h-3.5 w-3.5" /> : type === "packing" ? <Package className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />;
							return (
								<Button
									key={type}
									variant={queueType === type ? "default" : "outline"}
									size="sm"
									onClick={() => { setQueueType(type); setPage(1); setSelectedIds(new Set()); }}
									className="gap-1.5"
								>
									{icon}
									{type === "mrp" ? "MRP" : type === "packing" ? "Packing" : "Mappings"}
									{pending > 0 && <Badge variant="secondary" className="ml-1 text-xs px-1.5">{pending}</Badge>}
								</Button>
							);
						})}
						<div className="ml-auto flex items-center gap-2">
							<Button
								variant={quickWins ? "default" : "outline"}
								size="sm"
								onClick={() => { setQuickWins(!quickWins); setPage(1); }}
								className="gap-1"
							>
								<Zap className="h-3.5 w-3.5" />
								Quick Wins
							</Button>
							<Button variant="outline" size="sm" onClick={() => setShowShortcuts(!showShortcuts)} className="gap-1 dark:bg-gray-700 dark:border-gray-600">
								<Keyboard className="h-3.5 w-3.5" />
							</Button>
							<Button variant="outline" size="sm" onClick={handlePopulate} disabled={populating} className="gap-1 dark:bg-gray-700 dark:border-gray-600">
								{populating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
								Refresh Queue
							</Button>
						</div>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<Select value={priority} onValueChange={v => { setPriority(v); setPage(1); }}>
							<SelectTrigger className="w-[140px] h-8 text-xs dark:bg-gray-700 dark:border-gray-600">
								<SelectValue placeholder="Priority" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All priorities</SelectItem>
								<SelectItem value="high">High only</SelectItem>
								<SelectItem value="medium">Medium</SelectItem>
								<SelectItem value="low">Low</SelectItem>
							</SelectContent>
						</Select>
						<Select value={branchFilter} onValueChange={v => { setBranchFilter(v); setPage(1); }}>
							<SelectTrigger className="w-[200px] h-8 text-xs dark:bg-gray-700 dark:border-gray-600">
								<SelectValue placeholder="All branches" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All branches</SelectItem>
								{branches
									.filter((b: any) => b.branch_id && b.branch_name)
									.map((b: any) => (
									<SelectItem key={b.branch_id} value={String(b.branch_id)}>
										<span className="flex items-center gap-1.5">
											<span className={`inline-block w-1.5 h-1.5 rounded-full ${b.is_active ? "bg-green-500" : "bg-gray-400"}`} />
											{b.branch_name}
										</span>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<div className="flex items-center gap-1">
							<Input
								placeholder="Search product..."
								value={searchInput}
								onChange={e => setSearchInput(e.target.value)}
								onKeyDown={e => { if (e.key === "Enter") { setSearch(searchInput); setPage(1); } }}
								className="w-[200px] h-8 text-xs dark:bg-gray-700 dark:border-gray-600"
							/>
							<Button variant="outline" size="sm" className="h-8 w-8 p-0 dark:bg-gray-700 dark:border-gray-600" onClick={() => { setSearch(searchInput); setPage(1); }}>
								<Search className="h-3.5 w-3.5" />
							</Button>
						</div>
						<span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">{total} items</span>
					</div>
				</CardContent>
			</Card>

			{/* Bulk Actions Bar */}
			{selectedIds.size > 0 && (
				<Card className="dark:bg-gray-800 dark:border-gray-700 border-blue-200 dark:border-blue-700">
					<CardContent className="p-3 flex items-center gap-3">
						<Badge variant="secondary" className="text-xs">{selectedIds.size} selected</Badge>
						{queueType !== "mapping" ? (
							<>
								<Button size="sm" className="h-7 gap-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => handleAccept(Array.from(selectedIds))} disabled={!!actionLoading}>
									<Check className="h-3 w-3" /> Accept All
								</Button>
								<Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => handleReject(Array.from(selectedIds))} disabled={!!actionLoading}>
									<X className="h-3 w-3" /> Keep All Current
								</Button>
							</>
						) : (
							<>
								<Button size="sm" className="h-7 gap-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => handleAccept(Array.from(selectedIds))} disabled={!!actionLoading}>
									<X className="h-3 w-3" /> Unlink All
								</Button>
								<Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => handleConfirmMapping(Array.from(selectedIds))} disabled={!!actionLoading}>
									<Check className="h-3 w-3" /> Confirm All Correct
								</Button>
							</>
						)}
						<Button size="sm" variant="ghost" className="h-7 gap-1 text-gray-500" onClick={() => handleSkip(Array.from(selectedIds))} disabled={!!actionLoading}>
							<SkipForward className="h-3 w-3" /> Skip All
						</Button>
						<Button size="sm" variant="ghost" className="h-7 text-xs text-gray-400" onClick={() => setSelectedIds(new Set())}>
							Clear
						</Button>
					</CardContent>
				</Card>
			)}

			{/* Review Table */}
			<Card className="dark:bg-gray-800 dark:border-gray-700">
				<CardContent className="p-0" ref={listRef}>
					{loading ? (
						<div className="flex items-center justify-center h-40">
							<Loader2 className="h-6 w-6 animate-spin text-blue-500" />
						</div>
					) : items.length === 0 ? (
						<div className="text-center py-12">
							<CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-3" />
							<div className="text-gray-500 dark:text-gray-400 font-medium">Queue is empty</div>
							<div className="text-sm text-gray-400 dark:text-gray-500 mt-1">
								{totalPending > 0 ? "No items match your filters" : "Click 'Refresh Queue' to scan for issues"}
							</div>
						</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
										<th className="py-2 px-3 text-left w-8">
											<input
												type="checkbox"
												checked={selectedIds.size === items.length && items.length > 0}
												onChange={selectAll}
												className="rounded border-gray-300 dark:border-gray-600"
											/>
										</th>
										{queueType !== "mapping" && <th className="py-2 px-2 text-left w-8" />}
										<th className="py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Product</th>
										{queueType === "mapping" ? (
											<>
												<th className="py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Pharmacy Product</th>
												<th className="py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Mapped Product</th>
												<th className="py-2 px-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">MRP</th>
												<th className="py-2 px-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Match</th>
												<th className="py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Branch</th>
											</>
										) : (
											<>
												<th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">{queueType === "mrp" ? "Current MRP" : "Current"}</th>
												<th className="py-2 px-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 w-6" />
												<th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">{queueType === "mrp" ? "Pharmacy MRP" : "Suggested"}</th>
												<th className="py-2 px-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Agree</th>
											</>
										)}
										<th className="py-2 px-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400"></th>
										<th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Actions</th>
									</tr>
								</thead>
								<tbody className="divide-y dark:divide-gray-700">
									{items.map((item, idx) => (
										<ReviewRow
											key={item.id}
											item={item}
											index={idx}
											isFocused={focusedIndex === idx}
											isSelected={selectedIds.has(item.id)}
											isExpanded={expandedId === item.id}
											queueType={queueType}
											customMrpId={customMrpId}
											customValue={customValue}
											actionLoading={actionLoading}
											customInputRef={customInputRef}
											getPriorityBadge={getPriorityBadge}
											onToggleSelect={() => toggleSelect(item.id)}
											onToggleExpand={() => setExpandedId(expandedId === item.id ? null : item.id)}
											onFocus={() => setFocusedIndex(idx)}
											onAccept={() => handleAccept([item.id])}
											onReject={() => handleReject([item.id])}
											onConfirmMapping={() => handleConfirmMapping([item.id])}
											onSkip={() => handleSkip([item.id])}
											onStartCustom={() => { setCustomMrpId(item.id); setCustomValue(""); setTimeout(() => customInputRef.current?.focus(), 50); }}
											onCancelCustom={() => { setCustomMrpId(null); setCustomValue(""); }}
											onCustomValueChange={setCustomValue}
											onApplyCustom={handleCustomApply}
											editingPackingId={editingPackingId}
											editingPackingValue={editingPackingValue}
											editingPackingRef={editingPackingRef}
											onStartPackingEdit={(suggested: string) => { setEditingPackingId(item.id); setEditingPackingValue(suggested); setTimeout(() => editingPackingRef.current?.focus(), 50); }}
											onPackingValueChange={setEditingPackingValue}
											onSavePacking={() => handleSavePacking(item.id, item.suggested_value)}
											onCancelPackingEdit={() => { setEditingPackingId(null); setEditingPackingValue(""); }}
										/>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			{totalPages > 1 && (
				<div className="flex justify-center">
					<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
				</div>
			)}
		</div>
	);
}

// ─── Review Row Component (table row) ─────────────────────────────────────

function ReviewRow({
	item, index, isFocused, isSelected, isExpanded, queueType,
	customMrpId, customValue, actionLoading, customInputRef,
	getPriorityBadge,
	onToggleSelect, onToggleExpand, onFocus, onAccept, onReject,
	onConfirmMapping, onSkip, onStartCustom, onCancelCustom,
	onCustomValueChange, onApplyCustom,
	editingPackingId, editingPackingValue, editingPackingRef,
	onStartPackingEdit, onPackingValueChange, onSavePacking, onCancelPackingEdit,
}: {
	item: any; index: number; isFocused: boolean; isSelected: boolean;
	isExpanded: boolean; queueType: QueueType;
	customMrpId: string | null; customValue: string; actionLoading: string | null;
	customInputRef: React.RefObject<HTMLInputElement>;
	getPriorityBadge: (p: string) => JSX.Element;
	onToggleSelect: () => void; onToggleExpand: () => void; onFocus: () => void;
	onAccept: () => void; onReject: () => void; onConfirmMapping: () => void;
	onSkip: () => void; onStartCustom: () => void; onCancelCustom: () => void;
	onCustomValueChange: (v: string) => void; onApplyCustom: () => void;
	editingPackingId: string | null; editingPackingValue: string;
	editingPackingRef: React.RefObject<HTMLInputElement>;
	onStartPackingEdit: (suggested: string) => void; onPackingValueChange: (v: string) => void;
	onSavePacking: () => void; onCancelPackingEdit: () => void;
}) {
	const entityDetails = item.entity_details || [];
	const entities = Array.isArray(entityDetails) ? entityDetails : [entityDetails];
	const isCustomMode = customMrpId === item.id;
	const isEditingPacking = editingPackingId === item.id;
	const firstEntity = entities[0] as any;

	// Detect mixed packs across entities
	const entityPacks = entities.map((e: any) => (e.pack || "").toString().trim().toLowerCase()).filter(Boolean);
	const uniquePacks = Array.from(new Set(entityPacks));
	const hasMixedPacks = uniquePacks.length > 1;

	const rowBg = isFocused
		? "bg-blue-50/50 dark:bg-blue-950/30"
		: isSelected
		? "bg-blue-50/30 dark:bg-blue-950/15"
		: "hover:bg-gray-50 dark:hover:bg-gray-800/50";

	// Total column span for expanded row
	const colSpan = queueType === "mapping" ? 10 : 9;

	return (
		<>
			{/* Main row */}
			<tr
				data-index={index}
				className={`${rowBg} ${isFocused ? "ring-2 ring-blue-500 ring-inset" : ""} transition-colors cursor-pointer`}
				onClick={onFocus}
			>
				{/* Checkbox */}
				<td className="py-2.5 px-3 w-8">
					<input
						type="checkbox"
						checked={isSelected}
						onChange={e => { e.stopPropagation(); onToggleSelect(); }}
						className="rounded border-gray-300 dark:border-gray-600"
					/>
				</td>

				{/* Expand toggle — only for MRP/Packing (they have multi-entity details) */}
				{queueType !== "mapping" && (
					<td className="py-2.5 px-2 w-8">
						<button onClick={e => { e.stopPropagation(); onToggleExpand(); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
							{isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
						</button>
					</td>
				)}

				{/* Product — name + packing info + code + company */}
				<td className="py-2.5 px-3">
					<div className="flex items-center gap-2">
						<div className="min-w-0">
							<div className="flex items-center gap-1.5">
								<span className="font-medium text-sm dark:text-gray-200 truncate max-w-[300px]">{item.product_name}</span>
								{getPriorityBadge(item.priority)}
								{hasMixedPacks && queueType !== "mapping" && (
									<Badge variant="outline" className="text-[10px] border-orange-300 text-orange-600 dark:border-orange-700 dark:text-orange-400 gap-0.5">
										<AlertTriangle className="h-2.5 w-2.5" />Mixed Packs
									</Badge>
								)}
							</div>
							{(item.catalog_packing_info || item.catalog_packing) && queueType !== "mapping" && (
								<div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
									{item.catalog_packing_info || item.catalog_packing}
								</div>
							)}
							<div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
								{item.mb_code && <span className="font-mono">{item.mb_code}</span>}
								{item.company_name && <span className="ml-2">{item.company_name}</span>}
								{queueType === "packing" && item.catalog_mrp && (
									<span className="ml-2 font-mono font-medium text-gray-600 dark:text-gray-300">MRP: ₹{Number(item.catalog_mrp).toFixed(2)}</span>
								)}
							</div>
						</div>
					</div>
				</td>

				{queueType === "mapping" ? (
					<>
						{/* Pharmacy Name + code */}
						<td className="py-2.5 px-3">
							<div className="max-w-[200px]">
								<div className="text-sm dark:text-gray-200 truncate">{firstEntity?.entity_name || "—"}</div>
								<div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
									{firstEntity?.entity_code && <span className="font-mono">{firstEntity.entity_code}</span>}
									{firstEntity?.entity_mfr && <span className="ml-2 truncate">{firstEntity.entity_mfr}</span>}
								</div>
							</div>
						</td>

						{/* Mapped Product */}
						<td className="py-2.5 px-3">
							<div className="max-w-[200px]">
								<div className="text-sm text-blue-600 dark:text-blue-400 truncate">{firstEntity?.catalog_name || "—"}</div>
								<div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
									{firstEntity?.catalog_company && <span>{firstEntity.catalog_company}</span>}
								</div>
							</div>
						</td>

						{/* MRP comparison */}
						<td className="py-2.5 px-3 text-center">
							<div className="text-xs">
								<span className="font-mono dark:text-gray-300">₹{Number(firstEntity?.entity_mrp || 0).toFixed(0)}</span>
								<span className="text-gray-400 mx-1">/</span>
								<span className="font-mono text-blue-600 dark:text-blue-400">₹{Number(firstEntity?.catalog_mrp || 0).toFixed(0)}</span>
							</div>
						</td>

						{/* Match % */}
						<td className="py-2.5 px-3 text-center">
							<span className={`text-xs font-medium ${Number(item.confidence) < 20 ? "text-red-500" : Number(item.confidence) < 30 ? "text-orange-500" : "text-yellow-500"}`}>
								{Number(item.confidence).toFixed(0)}%
							</span>
						</td>

						{/* Branch + active */}
						<td className="py-2.5 px-3">
							<div className="flex items-center gap-1.5 text-xs">
								<span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${firstEntity?.is_active !== false ? "bg-green-500" : "bg-gray-400"}`} />
								<span className="dark:text-gray-300 truncate max-w-[120px]">{firstEntity?.branch_name || `Branch ${firstEntity?.branch_id}`}</span>
							</div>
						</td>
					</>
				) : (
					<>
						{/* Current value */}
						<td className="py-2.5 px-3 text-right">
							<span className={`font-mono text-sm ${queueType === "mrp" && (!item.current_value || Number(item.current_value) === 0) ? "text-gray-400 dark:text-gray-500 italic" : "dark:text-gray-300"}`}>
								{queueType === "mrp"
								? (!item.current_value || Number(item.current_value) === 0 ? "Not set" : `₹${Number(item.current_value).toFixed(2)}`)
								: (item.current_value || "—")}
							</span>
						</td>

						{/* Arrow */}
						<td className="py-2.5 px-1 text-center text-gray-300 dark:text-gray-600 w-6">→</td>

						{/* Suggested value */}
						<td className="py-2.5 px-3 text-right">
							{queueType === "packing" ? (
								isEditingPacking ? (
									<Input
										ref={editingPackingRef as any}
										value={editingPackingValue}
										onChange={e => onPackingValueChange(e.target.value)}
										onKeyDown={e => { if (e.key === "Enter") onSavePacking(); if (e.key === "Escape") onCancelPackingEdit(); }}
										className="w-36 h-7 text-xs font-mono dark:bg-gray-700 dark:border-gray-600 text-right"
										onClick={e => e.stopPropagation()}
									/>
								) : (
									<span
										className="font-mono text-sm font-semibold text-blue-600 dark:text-blue-400 cursor-pointer border-b border-dashed border-blue-300 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 px-1 py-0.5 rounded"
										onClick={e => { e.stopPropagation(); onStartPackingEdit(item.suggested_value || ""); }}
										title="Click to edit packing"
									>
										{item.suggested_value || "—"}
									</span>
								)
							) : (
								<span className="font-mono text-sm font-semibold text-blue-600 dark:text-blue-400">
									{`₹${Number(item.suggested_value || 0).toFixed(2)}`}
								</span>
							)}
						</td>

						{/* Agree count */}
						<td className="py-2.5 px-3 text-center">
							<span className="text-xs dark:text-gray-300">{item.entity_count}<span className="text-gray-400">/{entities.length}</span></span>
						</td>
					</>
				)}

				{/* Confidence/Quick win badge */}
				<td className="py-2.5 px-3 text-center">
					{queueType !== "mapping" && Number(item.confidence) >= 100 && (
						<Badge variant="outline" className="text-[10px] text-green-600 border-green-300 dark:text-green-400 dark:border-green-800">100%</Badge>
					)}
					{queueType === "mapping" && (
						<Badge variant="outline" className="text-[10px]">{firstEntity?.reason || "Flagged"}</Badge>
					)}
				</td>

				{/* Actions */}
				<td className="py-2.5 px-3 text-right">
					<div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
						{isCustomMode ? (
							<>
								<Input
									ref={customInputRef as any}
									value={customValue}
									onChange={e => onCustomValueChange(e.target.value)}
									onKeyDown={e => { if (e.key === "Enter") onApplyCustom(); if (e.key === "Escape") onCancelCustom(); }}
									placeholder={queueType === "mrp" ? "₹ amount" : "value"}
									className="w-20 h-7 text-xs dark:bg-gray-700 dark:border-gray-600"
								/>
								<Button size="sm" className="h-7 px-2" onClick={onApplyCustom} disabled={!customValue}><Check className="h-3 w-3" /></Button>
								<Button size="sm" variant="ghost" className="h-7 px-2" onClick={onCancelCustom}><X className="h-3 w-3" /></Button>
							</>
						) : queueType === "packing" ? (
							isEditingPacking ? (
								<>
									<Button size="sm" className="h-7 px-2 gap-1 bg-green-600 hover:bg-green-700 text-white text-xs" onClick={onSavePacking} disabled={!!actionLoading || !editingPackingValue.trim()}>
										<Check className="h-3 w-3" /> Save
									</Button>
									<Button size="sm" variant="ghost" className="h-7 px-2" onClick={onCancelPackingEdit}>
										<X className="h-3 w-3" />
									</Button>
								</>
							) : (
								<>
									<Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={onReject} disabled={!!actionLoading} title="Current packing is correct">
										Keep
									</Button>
									<Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400" onClick={onSkip} title="Skip for now">
										<SkipForward className="h-3 w-3" />
									</Button>
								</>
							)
						) : queueType === "mapping" ? (
							<>
								<Button size="sm" className="h-7 px-2 gap-1 bg-red-600 hover:bg-red-700 text-white text-xs" onClick={onAccept} disabled={!!actionLoading}>
									<X className="h-3 w-3" /> Unmap
								</Button>
								<Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-xs" onClick={onConfirmMapping} disabled={!!actionLoading}>
									<Check className="h-3 w-3" /> Correct
								</Button>
								<Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400" onClick={onSkip}>
									<SkipForward className="h-3 w-3" />
								</Button>
							</>
						) : (
							<>
								<Button size="sm" className="h-7 px-2 gap-1 bg-green-600 hover:bg-green-700 text-white text-xs" onClick={onAccept} disabled={!!actionLoading}>
									<Check className="h-3 w-3" /> Accept
								</Button>
								<Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={onReject} disabled={!!actionLoading}>
									Keep
								</Button>
								<Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-gray-500" onClick={onStartCustom}>
									Custom
								</Button>
								<Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400" onClick={onSkip}>
									<SkipForward className="h-3 w-3" />
								</Button>
							</>
						)}
					</div>
				</td>
			</tr>

			{/* Expanded detail row — pharmacy-level breakdown (MRP/Packing only) */}
			{isExpanded && queueType !== "mapping" && (
				<tr>
					<td colSpan={colSpan} className="p-0">
						<div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-2">
							<table className="w-full text-xs">
								<thead>
									<tr className="border-b dark:border-gray-700">
										<th className="py-1.5 px-2 text-left dark:text-gray-400 font-medium">Branch</th>
										<th className="py-1.5 px-2 text-center dark:text-gray-400 font-medium w-16">Status</th>
										<th className="py-1.5 px-2 text-left dark:text-gray-400 font-medium">Code</th>
										<th className="py-1.5 px-2 text-left dark:text-gray-400 font-medium">Product Name</th>
										{(queueType === "mrp" || queueType === "packing") && <th className="py-1.5 px-2 text-right dark:text-gray-400 font-medium">MRP</th>}
										<th className="py-1.5 px-2 text-left dark:text-gray-400 font-medium">Pack</th>
										<th className="py-1.5 px-2 text-left dark:text-gray-400 font-medium">Manufacturer</th>
									</tr>
								</thead>
								<tbody>
									{entities.map((ep: any, i: number) => (
										<tr key={i} className="border-b dark:border-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-800/50">
											<td className="py-1.5 px-2 dark:text-gray-200">{ep.branch_name || `Branch ${ep.branch_id}`}</td>
											<td className="py-1.5 px-2 text-center">
												<span className={`inline-flex items-center gap-1 text-xs ${ep.is_active !== false ? "text-green-600 dark:text-green-400" : "text-gray-400"}`}>
													<span className={`inline-block w-1.5 h-1.5 rounded-full ${ep.is_active !== false ? "bg-green-500" : "bg-gray-400"}`} />
													{ep.is_active !== false ? "Active" : "Inactive"}
												</span>
											</td>
											<td className="py-1.5 px-2 font-mono dark:text-gray-300">{ep.code || "—"}</td>
											<td className="py-1.5 px-2 dark:text-gray-300 truncate max-w-[200px]">{ep.name || "—"}</td>
											{(queueType === "mrp" || queueType === "packing") && (
												<td className={`py-1.5 px-2 text-right font-mono ${queueType === "mrp" && String(ep.mrp) === item.suggested_value ? "text-green-600 dark:text-green-400 font-medium" : "dark:text-gray-300"}`}>
													₹{Number(ep.mrp || 0).toFixed(2)}
												</td>
											)}
											<td className="py-1.5 px-2 dark:text-gray-300">{ep.pack || "—"}</td>
											<td className="py-1.5 px-2 dark:text-gray-300 truncate max-w-[180px]">{ep.mfr || "—"}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</td>
				</tr>
			)}
		</>
	);
}
